import { Id, isSuccess, useToasts } from "@dgs/core";
import { useQueryClient } from "@tanstack/react-query";
import { isPast, parseISO } from "date-fns";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { appNotificationService } from "../shared/api/appNotificationService";
import {
	AppNotificationActionType,
	AppNotificationFormValues,
	AppNotificationUpdateResource,
} from "../shared/types/appNotification";

export const useAppNotification = () => {
	const { t } = useTranslation();
	const { showToast } = useToasts();
	const queryClient = useQueryClient();
	const navigate = useNavigate();

	const validate = useCallback(
		(values: AppNotificationFormValues) => {
			const errors: { [K in keyof AppNotificationFormValues]?: string } = {};

			if (!values.title) {
				errors.title = t("Required field");
			} else if (values.title.length > 60) {
				errors.title = t("This field may have only max. 60 characters");
			}

			if (!values.body) {
				errors.body = t("Required field");
			} else if (values.title.length > 60) {
				errors.body = t("This field may have only max. 400 characters");
			}

			if (
				values.actionType !== AppNotificationActionType.NONE &&
				values.actionType !== AppNotificationActionType.REGISTRATION_LINK &&
				!values.actionValue
			) {
				errors.actionValue = t("Required field");
			}

			if (!values.recipientType) {
				errors.recipientType = t("Field is required");
			}

			if (values.showSendAt && !values.sendAt) {
				errors.sendAt = t("Field is required");
			}

			if (values.sendAt && isPast(parseISO(values.sendAt))) {
				errors.sendAt = t("Send date must be in the future.");
			}

			return errors;
		},
		[t],
	);

	const mapToValues = (notification: AppNotificationUpdateResource): AppNotificationFormValues => {
		return {
			actionType: notification.actionType,
			actionValue: notification.actionValue,
			recipientType: notification.recipientType,
			body: notification.body,
			title: notification.title,
			selectedRecipients: notification.selectedRecipients,
			event: notification.event,
			showSendAt: notification.showSendAt,
			sendAt: notification.sendAt,
		};
	};

	const setActionData = (type: AppNotificationActionType, actionValue: any) => {
		switch (type) {
			case AppNotificationActionType.OPEN_EVENT:
				return { eventId: actionValue.id };
			case AppNotificationActionType.OPEN_TAB:
				return { bottomNavBarItemId: actionValue.id };
			case AppNotificationActionType.OPEN_WEBSITE:
				return { url: actionValue };
			default:
				return null;
		}
	};

	const handleCreate = useCallback(
		async (values: AppNotificationFormValues) => {
			const response = await appNotificationService.store({
				title: values.title,
				body: values.body,
				recipientType: values.recipientType,
				recipients: {
					type: values.recipientType,
					selectedUserIds: values.selectedRecipients.map((recipient) => recipient.id),
					eventId: values.event ? values.event.id : null,
				},
				action: {
					type: values.actionType,
					data: setActionData(values.actionType, values.actionValue),
				},
				sendAt: values.sendAt,
			});

			if (isSuccess(response)) {
				await queryClient.invalidateQueries({ queryKey: appNotificationService.indexKey });
				showToast({
					type: "success",
					title: t("Notification"),
					body: t("Notification was saved."),
				});
				navigate("/app/notifications");
			}
		},
		[navigate, queryClient, showToast, t],
	);

	const handleUpdate = useCallback(
		async (id: Id, values: AppNotificationFormValues) => {
			const response = await appNotificationService.update(id, {
				title: values.title,
				body: values.body,
				recipientType: values.recipientType,
				recipients: {
					type: values.recipientType,
					selectedUserIds: values.selectedRecipients.map((recipient) => recipient.id),
					eventId: values.event ? values.event.id : null,
				},
				action: {
					type: values.actionType,
					data: setActionData(values.actionType, values.actionValue),
				},
				sendAt: values.sendAt,
			});

			if (isSuccess(response)) {
				await queryClient.invalidateQueries({ queryKey: appNotificationService.indexKey });
				await queryClient.invalidateQueries({ queryKey: appNotificationService.showKey(id) });
				showToast({
					type: "success",
					title: t("Notification"),
					body: t("Notification was saved."),
				});
				navigate("/app/notifications");
			}
		},
		[navigate, queryClient, showToast, t],
	);

	const handleDelete = useCallback(
		async (id: Id) => {
			const response = await appNotificationService.destroy(id);

			if (isSuccess(response)) {
				await queryClient.invalidateQueries({ queryKey: appNotificationService.indexKey });
				showToast({
					body: t("Notification was deleted."),
					title: t("Notification"),
					type: "success",
				});
				navigate("/app/notifications");
				return true;
			}
			return false;
		},
		[navigate, queryClient, showToast, t],
	);

	return {
		validate,
		mapToValues,
		handleCreate,
		handleUpdate,
		handleDelete,
	};
};
